// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dialog for exporting SRM analysis report.
 * Fetches real findings data and generates printable HTML.
 */

import { useState, useCallback, useEffect, type FC } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Label,
  Checkbox,
} from '@/components/ui'
import { toast } from 'sonner'
import type { NodeApiClient } from '@/app-shell/services'
import type { DocumentListItem } from '@/subsystems/documents'
import type { Agent } from '@/subsystems/agents'
import { useDatasets } from '@/subsystems/pipelines'
import type { DatasetDefinition } from '@/subsystems/pipelines'
import { fetchDatasetByKey } from '@/applications/shared'
import type { DatasetResponse } from '@/applications/shared'
import type { SrmRequest, SrmFinding, ExposureLevel } from '../types'
import { generateSrmReportHTML, openPrintWindow, type SrmReportTrack } from '../utils/print'
import { agentFindingsDatasetKeys, trackPalette } from '../utils/track'
import {
  buildChecklist,
  fetchKbCriteria,
  isEligibilityPayload,
  parseDetermination,
  parseCriteria,
  type KbCriterion,
} from '../utils/eligibility'

interface ExportReportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  request: SrmRequest
  agents: Agent[]
  apiClient: NodeApiClient
  orgId: string
  wsId: string
}

interface SrmFindingsPayload {
  findings?: Array<{
    knowledge_item?: string
    knowledgeItem?: string
    content?: string
    riskScore?: number | null
    risk_score?: number | null
    section_reference?: string
    sectionReference?: string
    source_text?: string
    sourceText?: string
    exposure?: string
    recommendation?: string
    page_number?: number
    pageNumber?: number
  }>
}

function parseExposureLevel(exposure: string | null | undefined): ExposureLevel | null {
  if (!exposure) return null
  const match = exposure.match(/^(NEGLIGIBLE|LOW|MODERATE|HIGH|SEVERE|CRITICAL):/i)
  return match ? match[1].toUpperCase() as ExposureLevel : null
}

/** One document's read of one of the track's datasets. */
interface DatasetHit {
  datasetKey: string
  response: DatasetResponse
}

/**
 * Every dataset a track's documents actually hold results for.
 *
 * A track's documents are analyzed by different nodes — the intake form carries the
 * eligibility determination, the vendor agreement the terms findings — so most document ×
 * dataset pairs never existed and 404. Those are dropped rather than failing the export.
 */
async function fetchTrackDatasets(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documents: DocumentListItem[],
  agentId: string,
  datasetKeys: string[],
  groupId: string,
): Promise<DatasetHit[]> {
  const hits = await Promise.all(
    documents.flatMap((doc) =>
      datasetKeys.map(async (datasetKey): Promise<DatasetHit | null> => {
        try {
          const response = await fetchDatasetByKey(apiClient, orgId, wsId, doc.documentId, agentId, datasetKey, doc.latestVersion, groupId)
          return response ? { datasetKey, response } : null
        } catch {
          return null
        }
      }),
    ),
  )
  return hits.filter((hit): hit is DatasetHit => hit !== null)
}

function parseFindings(response: DatasetResponse): SrmFinding[] {
  const findings = (response.payload as SrmFindingsPayload | undefined)?.findings ?? []
  return findings.map((f) => {
    const exposure = f.exposure ?? null
    return {
      kbItemId: f.knowledge_item ?? f.knowledgeItem ?? 'unknown',
      content: f.content ?? '',
      sectionReference: f.section_reference ?? f.sectionReference ?? null,
      sourceText: f.source_text ?? f.sourceText ?? null,
      exposure,
      exposureLevel: parseExposureLevel(exposure),
      recommendation: f.recommendation ?? null,
      pageNumber: f.page_number ?? f.pageNumber ?? null,
      riskScore: f.riskScore ?? f.risk_score ?? null,
    }
  })
}

/**
 * The KB criteria list for each eligibility dataset in play, keyed by dataset key.
 *
 * The model reports only the criteria it flags, so the printed checklist enumerates the
 * workspace's KB list the same way the dashboard panel does — a checklist missing the criteria
 * nobody flagged is not an audit trail.
 */
async function fetchChecklistCriteria(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  datasetKeys: string[],
  datasets: DatasetDefinition[],
): Promise<Map<string, KbCriterion[]>> {
  const byDatasetKey = new Map<string, KbCriterion[]>()
  await Promise.all(
    datasetKeys.map(async (datasetKey) => {
      const kbInstanceId = datasets.find((d) => d.key === datasetKey)?.knowledgeBase
      if (!kbInstanceId) return
      byDatasetKey.set(datasetKey, await fetchKbCriteria(apiClient, orgId, wsId, kbInstanceId))
    }),
  )
  return byDatasetKey
}

/** One agent's section of the report: its determination, if it gates on one, and its findings. */
async function buildReportTrack(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  request: SrmRequest,
  datasets: DatasetDefinition[],
  agent: Agent,
  icon: string,
): Promise<SrmReportTrack> {
  const slotIds = new Set(agent.slots.map((s) => s.id))
  const docs = request.documents.filter((d) => d.slotId && slotIds.has(d.slotId))
  const hits = await fetchTrackDatasets(
    apiClient, orgId, wsId, docs, agent.agentId, agentFindingsDatasetKeys(agent), request.groupId,
  )

  // Which half of the review a dataset holds is not in the track config, so each payload's own
  // shape decides: a determination plus criteria, or a findings list.
  const eligibilityHits = hits.filter((h) => isEligibilityPayload(h.response))
  const findingsHits = hits.filter((h) => !isEligibilityPayload(h.response))

  const criteriaByDatasetKey = await fetchChecklistCriteria(
    apiClient, orgId, wsId, [...new Set(eligibilityHits.map((h) => h.datasetKey))], datasets,
  )

  return {
    agentId: agent.agentId,
    label: agent.label,
    icon,
    findings: findingsHits.flatMap((h) => parseFindings(h.response)),
    eligibility: eligibilityHits.length > 0
      ? eligibilityHits.map((h) => ({
          determination: parseDetermination(h.response),
          criteria: buildChecklist(criteriaByDatasetKey.get(h.datasetKey) ?? [], parseCriteria(h.response)),
        }))
      : undefined,
  }
}

export const ExportReportDialog: FC<ExportReportDialogProps> = ({
  open,
  onOpenChange,
  request,
  agents,
  apiClient,
  orgId,
  wsId,
}) => {
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(
    () => new Set(agents.map((a) => a.agentId)),
  )
  const [includeRiskSummary, setIncludeRiskSummary] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  // Resolves an eligibility dataset to the KB instance holding its criteria list.
  const { datasets } = useDatasets(orgId, wsId)

  // Re-sync default selection when the agent list changes (workspace switch)
  useEffect(() => {
    setSelectedAgents(new Set(agents.map((a) => a.agentId)))
  }, [agents])

  const handleAgentToggle = useCallback((agentId: string) => {
    setSelectedAgents((prev) => {
      const next = new Set(prev)
      if (next.has(agentId)) {
        next.delete(agentId)
      } else {
        next.add(agentId)
      }
      return next
    })
  }, [])

  const handleExport = useCallback(async () => {
    setIsExporting(true)

    try {
      const tracks = await Promise.all(
        agents
          .filter((a) => selectedAgents.has(a.agentId))
          .map((agent, idx) =>
            buildReportTrack(
              apiClient, orgId, wsId, request, datasets, agent, trackPalette(idx).printIcon,
            ),
          ),
      )

      const html = generateSrmReportHTML({
        request,
        tracks,
        includeRiskSummary,
      })

      openPrintWindow(html)
      onOpenChange(false)
    } catch (err) {
      toast.error('Export failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setIsExporting(false)
    }
  }, [apiClient, orgId, wsId, request, agents, datasets, selectedAgents, includeRiskSummary, onOpenChange])

  const canExport = selectedAgents.size > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="srm-export-dialog">
        <DialogHeader>
          <DialogTitle>Export Report</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-3">
            <Label>Include</Label>
            <div className="space-y-2">
              {agents.map((agent) => (
                <label
                  key={agent.agentId}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedAgents.has(agent.agentId)}
                    onCheckedChange={() => handleAgentToggle(agent.agentId)}
                  />
                  <span className="text-sm">{agent.label} Findings</span>
                </label>
              ))}
              <label className="flex items-center gap-3 cursor-pointer pt-2 border-t">
                <Checkbox
                  checked={includeRiskSummary}
                  onCheckedChange={(checked) =>
                    setIncludeRiskSummary(checked === true)
                  }
                />
                <span className="text-sm">Overall Risk Summary</span>
              </label>
            </div>
          </div>

          <div className="space-y-3">
            <Label>Preview</Label>
            <div className="border rounded-md p-4 bg-muted text-sm space-y-2">
              <div className="font-semibold">
                SRM Analysis Report: {request.groupId}
              </div>
              {request.title && (
                <div className="text-muted-foreground">
                  Title: {request.title}
                </div>
              )}
              <div className="text-muted-foreground">
                Date: {new Date().toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
              <div className="pt-2 text-xs text-muted-foreground">
                {selectedAgents.size} track{selectedAgents.size !== 1 ? 's' : ''} selected
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={!canExport || isExporting}
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Print Report
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
